import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ElementService } from '../../../resources/element/element.service';
import { map, takeUntil, take, skip } from 'rxjs/operators';
import { ProjectElement } from '../../../resources/element/project-element.interface';
import { ModuleService } from '../../../resources/module/module.service';
import { ProjectModule } from '../../../resources/module/project-module.interface';
import { SubmoduleService } from '../../../resources/submodule/submodule.service';
import { ProjectSubmodule } from '../../../resources/submodule/project-submodule.interface';

import { get } from 'lodash-es';
import { ConstantsProjectLevel } from '../../../utilities/constants/constants.interface';
import { ProjectService } from '../../../resources/project/project.service';
import { BuildingService } from '../../../resources/building/building.service';
import { AreaService } from '../../../resources/area/area.service';
import { BasicAreaService } from '../../../resources/basic-area/basic-area.service';

import { UserService } from '../../../resources/user/user.service';
import { User } from '../../../resources/user/user.interface';
import { Subject } from 'rxjs';
import { ElementSidebarService } from '../../../resources/element/element-sidebar/element-sidebar.service';
import { RouterService } from '../../../utilities/router/router.service';
import { TableFilterService } from '../../../utilities/filters/table-filters.service';
import { environment } from '../../../../environments/environment';

/**
 * Elements table.
 */
@Component({
  selector: 'sg2-elements-table',
  templateUrl: './elements-table.component.html',
  styleUrls: ['./elements-table.component.less']
})
export class ElementsTableComponent implements AfterViewInit, OnDestroy, OnInit {

  /** Material paginator/ */
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /** Material sort. */
  @ViewChild(MatSort) sort: MatSort;

  /** List of constructions for quick local lookup. */
  public constructions: ConstantsProjectLevel[];

  /** Unsubscribe hook. */
  private destroy$ = new Subject();

  /** Default table columns. */
  public displayedColumns: string[] = ['code', 'name', 'customValues.constructionName',
    'customValues.moduleName', 'customValues.userName', 'edited', 'options'];

  /** Material table data source. */
  public elements = new MatTableDataSource();

  /** List of modules for quick local lookup. */
  public modules: ProjectModule[];

  /** List of submodules for quick local lookup. */
  public submodules: ProjectSubmodule[];

  /** List of users for quick local lookup. */
  public users: User[];

  constructor(
    private areaService: AreaService,
    private basicAreaService: BasicAreaService,
    private buildingService: BuildingService,
    private elementService: ElementService,
    public tableFilterService: TableFilterService,
    private moduleService: ModuleService,
    private projectService: ProjectService,
    private routerService: RouterService,
    public sidebarService: ElementSidebarService,
    private submoduleService: SubmoduleService,
    private userService: UserService
  ) {
    this.modules = this.moduleService.get();
    this.submodules = this.submoduleService.get();
    this.users = this.userService.get();
  }

  ngOnInit() {
    this._initFilters();
    this._trackFilters();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.tableFilterService.clear();
  }

  ngAfterViewInit() {
    this.elements.paginator = this.paginator;
    this.elements.sort = this.sort;
    this.elements.filterPredicate = this._searchPredicate;
    this.elements.sortingDataAccessor = this._sortingDataAccessor;

    this._trackElements();
  }

  /**
   * Go to construction/page.
   *
   * @todo After migration to new Router, instead of forwarding to correct page,
   *  it's better to return an url to the template, so when hovering the link,
   *  it's shows the url in the bottom left of the browser. Way better UX.
   *
   * @param element The element used to locate the correct page.
   */
  public goToConstruction(element: ProjectElement): void {
    switch (element.projectLevel) {
      case 1:
        this.routerService.goToPage(element, 'pve');
        break;
      case 2:
        this.routerService.goToPage(element, 'building');
        break;
      case 4:
        this.routerService.goToPage(element, 'basicArea');
        break;
      case 5:
        this.routerService.goToPage(element, 'area');
        break;
    }
  }

  /**
   * Go to element's page.
   *
   * When possible, go to element's location on that page too.
   */
  public goToElement(element: ProjectElement): void {
    switch (element.projectLevel) {
      case 1:
        this.routerService.goToElement(element, 'pve');
        break;
      case 2:
        this.routerService.goToElement(element, 'pve');
        break;
      case 4:
        this.routerService.goToElement(element, 'basicArea');
        break;
      case 5:
        this.routerService.goToElement(element, 'area');
        break;
    }
  }

  /**
   * Logs element when clicking on its 'code' on the table.
   *
   * This was meant for debugging ticket SPEC-2583, but it's actually quite
   *  convenient for debugging other tickets too, since the elements page
   *  displays *every* element in a project.
   *
   * @note It's *not* added to production because of the environment variable.
   *
   * @param element Project element.
   */
  public log(element: ProjectElement): void {
    if (!environment.production) {
      console.log(element);
    }
  }

  /**
   * On page change.
   *
   * When changing page (paginator), scroll to top.
   */
  public onChangePage($event: PageEvent): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Search.
   *
   * @param value The value to find.
   */
  public search(value: string): void {
    this.elements.filter = value.trim().toLocaleLowerCase();
    this.elements.paginator.firstPage();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Get construction.
   *
   * @note Fallback has been added for projects where elements are `projectLevel: 2`,
   *  but `constructionId` points to project.
   * @todo Fallback should be removed once resolved in ticket SPEC-2604.
   *
   * @todo Create and move to seperate helper service 'ConstructionService'.
   *
   * @param element The element to get the construction for.
   * @returns The name of the corresponding construction.
   */
  private _getConstruction(element: ProjectElement): string {
    const { constructionId, projectLevel } = element;
    const fallback = 'unknown name';
    switch (projectLevel) {
      case 1:
        return this.projectService.getName() || fallback;
      case 2:
        return this.buildingService.getBuildingProperties(constructionId).name || fallback;
      case 4:
        return this.basicAreaService.getBasicArea(constructionId).name || fallback;
      case 5:
        return this.areaService.getAreaProperties(constructionId).name || fallback;
    }
  }

  /**
   * Get construction type
   *
   * @todo Create and move to seperate helper service 'ConstructionService'.
   *
   * @param element The element to get the construction type for.
   * @returns The name of the corresponding construction type.
   */
  private _getConstructionType(element: ProjectElement): string {
    const { projectLevel } = element;
    switch (projectLevel) {
      case 1:
        return 'project';
      case 2:
        return 'building';
      case 4:
        return 'areatemplate';
      case 5:
        return 'area';
    }
  }

  /**
   * Add some custom values to the elements.
   *
   * The cleanest option would be to add a set of getters/pipes in the table template, but..
   * - 'Behind the scenes' it's still the default value, so sorting doesn't work;
   * - Huge performance hit: pre-parsing is a lot faster *and* makes for faster rendering/pagination.
   *
   * The values are added to the `customValues` property of an element, which does not have
   * an interface, and allows for easy cleanup.
   *
   * @note When there is no user assigned, an invisible character (omega) is used, to fix sorting:
   *  When using `null`, sorting will be 'null, null, null, A, B, C', which is not deisred, because
   *  it should be 'A, B, C, null, null, null'. To force this behaviour, a character that sorts
   *  after 'Z' is used.
   *
   * @param elements List of all elements
   * @returns List of elements with added values.
   */
  private _enrich(elements: ProjectElement[]): ProjectElement[] {
    for (const element of elements) {
      const module = this.modules.find(m => m._id === element.moduleId);
      const submodule = this.submodules.find(s => s._id === element.submoduleId);
      element.customValues = {
        constructionName: this._getConstruction(element),
        constructionType: this._getConstructionType(element),
        moduleName: `${ module.chapter ? module.chapter + '. ' : '' }${ module.name }`,
        submoduleName: `${ module.chapter && submodule.chapter ? submodule.chapter + '. ' : '' }${ submodule.name }`,
        userName: get(this.users.find(u => u._id === element.assignedToUserId), 'fullName')
      };
    }
    return elements;
  }

  /**
   * Create filters tree for table.
   *
   * @returns Filters collection in JSON for table.
   */
  private _initFilters(): void {
    this.tableFilterService.initFilters({
      construction: [
        this.tableFilterService.getProjects(),
        this.tableFilterService.getBuildings(),
        this.tableFilterService.getAreas(),
        this.tableFilterService.getAreaTemplates()
      ].filter(Boolean),
      chapter: this.tableFilterService.getModulesWithSubmodules(),
      user: [
        this.tableFilterService.getUsers(),
        this.tableFilterService.getComments()
      ],
      status: [
        this.tableFilterService.getStatuses()
      ]
    });
  }

  /**
   * Nested search function used by the predicate.
   *
   * @param search Data blob as string, used to search current term.
   * @param data Object to search in, either an element or a nested child object.
   * @param key Search string.
   * @returns Saerched/filtered data blob.
   */
  private _nestedSearchCheck(search: string, data: any, key: string): string {
    if (typeof data[key] === 'object') {
      for (const k in data[key]) {
        if (data[key][k] !== null) {
          search = this._nestedSearchCheck(search, data[key], k);
        }
      }
    } else {
      search += data[key];
    }
    return search;
  }

  /**
   * Filter predicate for table.
   *
   * The default filtering is extented by an algorithm which supports nested
   * objects/values.
   *
   * @param data Data to search.
   * @param filter Search query.
   * @returns Whether the string was found.
   */
  private _searchPredicate = (data: any, filter: string): boolean => {
    const accumulator = (currentTerm, key) => {
      return this._nestedSearchCheck(currentTerm, data, key);
    };
    const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
    const transformedFilter = filter.trim().toLowerCase();
    return dataStr.indexOf(transformedFilter) !== -1;
  }

  /**
   * Custom sorter.
   *
   * Not all rows were sorting correctly, so some cases can have a custom sorting function.
   *
   * Fallback is Lodash get, which is able to handle nested properties.
   *
   * @param element The element to sort.
   * @param property The property to sort by.
   * @returns Formatted search string.
   */
  private _sortingDataAccessor = (element: ProjectElement, property: string): any => {
    switch (property) {
      case 'code':
        return element.identifier;
      case 'edited':
        return new Date(element.updated);
      default:
        return get(element, property, 'Ω');
    }
  }

  /**
   * Track element store.
   */
  private _trackElements(): void {
    this.elementService.element$.pipe(
      takeUntil(this.destroy$),
      map((items: ProjectElement[]) => items.filter(item => item.active)),
      map((items: ProjectElement[]) => this.tableFilterService.filter(items))
    ).subscribe(elements => {
      this.elements.data = this._enrich(elements);
    });
  }

  /**
   * Track current active filters from service.
   */
  private _trackFilters(): void {
    this.tableFilterService.filter$.pipe(
      skip(1),
      takeUntil(this.destroy$)
    ).subscribe(f => {
      this.elementService.element$.pipe(
        take(1),
        map((items: ProjectElement[]) => items.filter(item => item.active)),
        map((items: ProjectElement[]) => this.tableFilterService.filter(items))
      ).subscribe(elements => {
        this.elements.data = this._enrich(elements);
      });
    });
  }
}
